import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { JustForYouTag } from "@/atoms/tag/JustForYouTag";
import type { ListingTypeOptions } from "@/utils/productTypes";

export interface RecommendationSlideData {
  id: string;
  type: ListingTypeOptions;
  image: string;
  title: string;
  price: string;
  memberPrice: string | null;
}

interface RecommendationSlideProps {
  data: RecommendationSlideData;
}

export const RecommendationSlide = ({
  data: { id, type, image, title, price, memberPrice },
}: RecommendationSlideProps) => {
  const navigate = useNavigate();
  const startPos = useRef<{ x: number; y: number } | null>(null);

  const handleClick = () => {
    const isProduct =
      type === "Product" || type === "ProductFromExternalSource";
    const isMembership = type === "Membership";

    navigate(
      isProduct
        ? `/pdp/product/${id}`
        : isMembership
        ? `/pdp/membership/${id}`
        : `/pdp/package/${id}`
    );
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!startPos.current) {
      return;
    }

    const deltaX = Math.abs(e.clientX - startPos.current.x);
    const deltaY = Math.abs(e.clientY - startPos.current.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Only trigger click if movement is less than 10 pixels (tap, not swipe)
    if (distance < 10) {
      handleClick();
    }

    startPos.current = null;
  };

  return (
    <div
      className="flex h-full w-full cursor-pointer flex-col overflow-hidden bg-white shadow-subtle transition-all duration-200 active:scale-[0.98]"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="absolute left-0 right-0 top-0 flex flex-shrink-0 justify-center pt-2">
        <JustForYouTag variant="in-carousel-card" />
      </div>

      <div className="mx-4 mb-1 mt-10 flex flex-1 flex-none items-center justify-center overflow-hidden rounded-md">
        <img
          src={image}
          alt={title}
          className="h-[150px] w-full object-cover"
        />
      </div>

      <h2 className="my-4 line-clamp-3 flex-shrink-0 px-4 text-center font-serif text-2xl font-medium leading-tight text-primary">
        {title}
      </h2>

      <div className="mt-auto flex flex-shrink-0 flex-col items-center justify-center gap-1 bg-neutral-50 py-3.5">
        <div className="flex items-center gap-2">
          <span className="text-sm leading-tight text-gray-600">
            From {price}
          </span>
          {memberPrice && (
            <>
              <div className="h-4 w-px bg-primary" />
              <span className="text-sm leading-tight text-primary">
                {memberPrice} Member
              </span>
            </>
          )}
        </div>
        <div
          className="text-xs text-primary underline"
          aria-label="View treatment details"
        >
          View
        </div>
      </div>
    </div>
  );
};
